// The asset page's Service card (CR_20260921_Asset_Lifecycle T3, §7.1): only when the asset's category has a service
// rule (Q4 — a steel plate gets no card at all). Each rule: next due (computed, L18), last done; then the last few
// jobs — the cost column only for managers, because RLS gives nobody else a cost to show (L19). "Log service" for a
// manager or warehouse staff at the asset's site (§4.5). Its history is the Lifecycle tab.
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DOC_TYPES, docProblem } from '@fleetspoke/core/lifecycle';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { openDocument } from '../../data/certificates';
import {
  SERVICE_KINDS,
  type ServiceKind,
  type ServiceRecord,
  useAssetService,
  useLogService,
  useSaveServiceCost,
} from '../../data/service';
import { calendarDate } from '../../lib/time';
import { money } from '../AssetValue';
import { Card, CardHeader } from './Card';

const STATE_COLOR = { ok: 'success', due_soon: 'warning', overdue: 'error' } as const;

export function ServiceCard({
  assetId,
  companyId,
  manager,
  onLog,
}: {
  assetId: string;
  companyId: string;
  manager: boolean;
  /** absent when the viewer may not log (§4.5) */
  onLog?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const svc = useAssetService(assetId);
  const [costing, setCosting] = useState<ServiceRecord | null>(null);
  if (!svc.data?.status.length) return null;
  const lang = i18n.language;
  return (
    <Card data-testid="asset-service">
      <CardHeader
        title={t('svc.title')}
        aside={
          onLog ? (
            <Button size="small" onClick={onLog} data-testid="service-log">
              {t('svc.log')}
            </Button>
          ) : null
        }
      />
      <Stack spacing={1} sx={{ mb: svc.data.records.length ? 2 : 0 }}>
        {svc.data.status.map((s) => (
          <Stack
            key={s.rule_id}
            direction="row"
            spacing={1.5}
            alignItems="center"
            data-testid="service-rule"
          >
            <Chip size="small" color={STATE_COLOR[s.state]} label={t(`svc.state_${s.state}`)} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {s.task} · {t('svc.next', { date: calendarDate(s.due_on, lang) ?? '—' })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {s.last_done_on
                  ? t('svc.last', { date: calendarDate(s.last_done_on, lang) })
                  : t('svc.never')}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
      {svc.data.records.length ? (
        <>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
            {t('svc.recent')}
          </Typography>
          <Stack spacing={0.75}>
            {svc.data.records.map((r) => (
              <Stack
                key={r.id}
                direction="row"
                spacing={1}
                alignItems="center"
                data-testid="service-record"
              >
                <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                  {[
                    calendarDate(r.done_on, lang),
                    t(`svc.kind_${r.kind}`),
                    r.task ?? t('svc.no_task'),
                    r.supplier_name,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
                {r.document_path ? (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => void openDocument(r.document_path!)}
                  >
                    {t('certs.open')}
                  </Link>
                ) : null}
                {/* money is managers' only: the column is not there for anyone else (L19) */}
                {manager ? (
                  r.cost ? (
                    <Typography variant="body2" sx={{ fontWeight: 500 }} data-testid="service-cost">
                      {money(r.cost.amount, r.cost.currency, lang)}
                    </Typography>
                  ) : (
                    <Link component="button" variant="body2" onClick={() => setCosting(r)}>
                      {t('svc.cost_add')}
                    </Link>
                  )
                ) : null}
              </Stack>
            ))}
          </Stack>
        </>
      ) : null}
      {costing ? (
        <CostDialog
          assetId={assetId}
          companyId={companyId}
          record={costing}
          onClose={() => setCosting(null)}
        />
      ) : null}
    </Card>
  );
}

function CostDialog({
  assetId,
  companyId,
  record,
  onClose,
}: {
  assetId: string;
  companyId: string;
  record: ServiceRecord;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const save = useSaveServiceCost(companyId, assetId);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('MYR');
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('svc.cost_add')}</DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={2}>
          <TextField
            label={t('svc.cost')}
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            autoFocus
            fullWidth
            slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
          />
          <TextField
            label={t('value.currency')}
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
            sx={{ width: 100 }}
          />
        </Stack>
        {save.error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {(save.error as Error).message}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={amount === '' || Number(amount) < 0 || save.isPending}
          onClick={() =>
            save.mutate({ recordId: record.id, amount, currency }, { onSuccess: onClose })
          }
        >
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export function LogServiceDialog({
  assetId,
  companyId,
  manager,
  tasks,
  today,
  onClose,
}: {
  assetId: string;
  companyId: string;
  manager: boolean;
  /** the category's service tasks, offered first */
  tasks: string[];
  today: string;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const log = useLogService(companyId, assetId);
  const [kind, setKind] = useState<ServiceKind>('service');
  const [task, setTask] = useState(tasks[0] ?? '');
  const [doneOn, setDoneOn] = useState(today);
  const [supplier, setSupplier] = useState('');
  const [note, setNote] = useState('');
  const [document, setDocument] = useState<File | null>(null);
  const [cost, setCost] = useState('');
  const [currency, setCurrency] = useState('MYR');
  const problem = document ? docProblem(document) : null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    log.mutate(
      { kind, task, doneOn, supplier, note, document, cost, currency },
      { onSuccess: onClose },
    );
  };
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={submit}>
        <DialogTitle>{t('svc.log_menu')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label={t('svc.kind')}
                value={kind}
                onChange={(e) => setKind(e.target.value as ServiceKind)}
                fullWidth
              >
                {SERVICE_KINDS.map((k) => (
                  <MenuItem key={k} value={k}>
                    {t(`svc.kind_${k}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                type="date"
                label={t('svc.done_on')}
                value={doneOn}
                onChange={(e) => setDoneOn(e.target.value)}
                required
                fullWidth
                slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: today } }}
              />
            </Stack>
            <TextField
              select={tasks.length > 0}
              label={t('svc.task')}
              value={task}
              onChange={(e) => setTask(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 60 } }}
            >
              {tasks.length
                ? [...tasks, ''].map((x) => (
                    <MenuItem key={x || '-'} value={x}>
                      {x || t('svc.no_task')}
                    </MenuItem>
                  ))
                : null}
            </TextField>
            <TextField
              label={t('svc.supplier')}
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 120 } }}
            />
            <TextField
              label={t('svc.note')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              minRows={2}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
            <Box>
              <Button component="label" variant="outlined" size="small">
                {t('svc.document')}
                <input
                  hidden
                  type="file"
                  accept={DOC_TYPES.join(',')}
                  data-testid="service-doc"
                  onChange={(e) => setDocument(e.target.files?.[0] ?? null)}
                />
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {document?.name ?? ''}
              </Typography>
              {problem ? (
                <Typography variant="caption" color="error" component="div">
                  {t(problem === 'too_big' ? 'certs.file_too_big' : 'certs.file_type')}
                </Typography>
              ) : null}
            </Box>
            {manager ? (
              <Stack direction="row" spacing={2}>
                <TextField
                  label={`${t('svc.cost')} (${t('common.optional')})`}
                  type="number"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                  fullWidth
                  slotProps={{
                    htmlInput: { min: 0, step: '0.01', 'data-testid': 'service-cost-input' },
                  }}
                />
                <TextField
                  label={t('value.currency')}
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase().slice(0, 3))}
                  sx={{ width: 100 }}
                />
              </Stack>
            ) : null}
            {log.error ? (
              <Alert severity="error">
                {t('common.error_generic')} {(log.error as Error).message}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!doneOn || doneOn > today || !!problem || log.isPending}
            data-testid="service-save-log"
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
